import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Link, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { ToastProvider } from "./context/ToastContext";
import { AuthProvider, useAuth } from "./context/AuthContext";
import Header from "./components/Header";
import { StreamerProvider } from "./components/Streamer";
import AuthModal from "./components/AuthModal";
import ContactForm, { ContactLink } from "./components/ContactForm";
import InviteListener from "./components/InviteListener";
import RejoinListener, { RoomCleanup } from "./components/RejoinListener";
import AdblockWall from "./components/AdblockWall";
import CookieBanner from "./components/CookieBanner";
import NewYear from "./components/NewYear";
import VerifyEmail from "./components/VerifyEmail";
import Home from "./pages/Home";
import GamePage from "./pages/GamePage";
import { Spinner } from "./components/ui";
import { useSeo } from "./lib/seo";
import { openConsent } from "./lib/consent";
import { LEGAL } from "./lib/legal";
import { useI18n } from "./i18n";
import { DEFAULT_LANG } from "./i18n/languages";
import { LANG_PREFIXES, isPublicPath, langFromPath, stripLang, withLang } from "./i18n/paths";
import { isTvTab, leaveTvMode } from "./lib/tvTab";
import { EVENTS, useEvent } from "./lib/season";
import "./App.css";

// Rooms (all the games) and the legal pages download when first opened.
import DiscordLink from "./components/DiscordLink";
import BanGate from "./components/BanWall";
import Pic from "./components/pics";
const Room = lazy(() => import("./pages/Room"));
const TvStart = lazy(() => import("./pages/TvStart"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const About = lazy(() => import("./pages/About"));
const Guides = lazy(() => import("./pages/Guides"));
const Guide = lazy(() => import("./pages/Guides").then((m) => ({ default: m.Guide })));
const Impressum = lazy(() => import("./pages/Impressum"));
const EmailAction = lazy(() => import("./pages/EmailAction"));
const Admin = lazy(() => import("./pages/Admin"));
const Casino = lazy(() => import("./pages/Casino"));

// During a holiday event, its decorations float behind the page (and snow
// falls at Christmas). Their places and moves are in App.css.
function BackgroundDoodles() {
  const event = EVENTS[useEvent()];
  return (
    <div className="bg-doodles" aria-hidden data-motion={event?.motion}>
      {event?.decor.map((s, i) => (
        <i key={i} className={`decor decor-${i}`}>
          <Pic name={s} />
        </i>
      ))}
      {event?.fall &&
        Array.from({ length: 14 }, (_, i) => (
          <i key={`f${i}`} className={`flake flake-${i}`}>
            <Pic name={event.fall} />
          </i>
        ))}
    </div>
  );
}

function NotFound() {
  const { t, lang } = useI18n();
  useSeo({ title: `${t("Page not found")} - PartyCade`, noindex: true });
  return (
    <div className="room-message panel">
      <div className="room-message-emoji">
        <Pic name="cards" />
      </div>
      <h2>{t("This card isn't in the deck")}</h2>
      <p className="muted">{t("That page doesn't exist.")}</p>
      <Link to={withLang(lang, "/")} className="btn btn-primary">
        {t("Back home")}
      </Link>
    </div>
  );
}

// Public pages have an address per language (/de/…, /ar/…). Opening one
// switches the site to its language; opening an English page while reading
// another language goes to that language's version.
function LanguageFromAddress() {
  const { lang, setLang } = useI18n();
  const { pathname, search, hash } = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    const base = stripLang(pathname);
    if (!isPublicPath(base)) return;
    const urlLang = langFromPath(pathname);
    if (urlLang) {
      if (urlLang !== lang) setLang(urlLang);
    } else if (lang !== DEFAULT_LANG) {
      navigate(withLang(lang, base) + search + hash, { replace: true });
    }
    // Only when the address changes: picking a language moves the address itself.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);
  return null;
}

// Email sign-ups verify their address before using the site (the legal
// pages stay open).
function VerifiedOnly({ children }) {
  const { needsVerification } = useAuth();
  const { pathname } = useLocation();
  // The legal pages stay open, and so does the page the verification link opens.
  if (needsVerification && !["/privacy", "/terms", "/impressum", "/auth/action"].includes(stripLang(pathname))) return <VerifyEmail />;
  return children;
}

// A TV tab is only a TV on the TV pages (/tv and the room). Walking anywhere
// else (the home page, a game page) leaves TV mode, so the tab is a normal
// one again and your own account comes back.
function TvTabGuard() {
  const { pathname } = useLocation();
  useEffect(() => {
    if (isTvTab() && !/^\/(tv|r)(\/|$)/.test(stripLang(pathname))) leaveTvMode();
  }, [pathname]);
  return null;
}

export default function App() {
  return (
    <BrowserRouter>
      <ToastProvider>
        <StreamerProvider>
          <AuthProvider>
            {/* A banned player sees only a timer (components/BanWall.js). */}
            <BanGate>
            <LanguageFromAddress />
            <TvTabGuard />
            <div className="app">
              <BackgroundDoodles />
              <Header />
              <main className="app-main">
                <VerifiedOnly>
                  <Suspense fallback={<Spinner />}>
                    <Routes>
                      {LANG_PREFIXES.flatMap((p) => [
                        <Route key={`${p}home`} path={p || "/"} element={<Home />} />,
                        <Route key={`${p}game`} path={`${p}/games/:slug`} element={<GamePage />} />,
                        <Route key={`${p}privacy`} path={`${p}/privacy`} element={<Privacy />} />,
                        <Route key={`${p}terms`} path={`${p}/terms`} element={<Terms />} />,
                      ])}
                      <Route path="/r/:code" element={<Room />} />
                      <Route path="/tv" element={<TvStart />} />
                      <Route path="/about" element={<About />} />
                      <Route path="/guides" element={<Guides />} />
                      <Route path="/guides/:slug" element={<Guide />} />
                      <Route path="/impressum" element={<Impressum />} />
                      <Route path="/auth/action" element={<EmailAction />} />
                      <Route path="/admin" element={<Admin />} />
                      <Route path="/casino" element={<Casino />} />
                      <Route path="*" element={<NotFound />} />
                    </Routes>
                  </Suspense>
                </VerifiedOnly>
              </main>
              <Footer />
            </div>
            <AuthModal />
            <ContactForm />
            <InviteListener />
            <RejoinListener />
            <RoomCleanup />
            <AdblockWall />
            <CookieBanner />
            <NewYear />
            </BanGate>
          </AuthProvider>
        </StreamerProvider>
      </ToastProvider>
    </BrowserRouter>
  );
}

function Footer() {
  const { t, lang } = useI18n();
  return (
    <footer className="site-footer">
      <nav className="footer-links" aria-label={t("Legal")}>
        <Link to={withLang(lang, "/privacy")}>{t("Privacy")}</Link>
        <Link to={withLang(lang, "/terms")}>{t("Terms")}</Link>
        <Link to="/impressum">Impressum</Link>
        <button className="link" onClick={openConsent}>
          {t("Cookie settings")}
        </button>
        <Link to="/about">{t("About")}</Link>
        <Link to="/guides">{t("Guides")}</Link>
        <ContactLink>{t("Contact")}</ContactLink>
        <ContactLink topic="report">{t("Report a problem")}</ContactLink>
        <DiscordLink />
      </nav>
      <span>
        {t("Made for game nights")} · <b>{LEGAL.site}</b> © {new Date().getFullYear()}
        {/* The casino's hidden door: barely visible until you look for it. */}
        <Link to="/casino" className="secret-door" aria-label={t("Casino")} title="♠">
          <Pic name="spade" />
        </Link>
      </span>
    </footer>
  );
}
